import React from "react";
import PropTypes from "prop-types";
import "./index.css";

const formatAmount = (amount) =>
  typeof amount === "number" ? `₹${amount.toLocaleString("en-IN")}` : amount;

/**
 * Rupees knocked off a tier, so the discount can be stated as a concrete number
 * rather than left for the customer to work out from the struck fare. Null
 * unless both figures are numeric and the strike is genuinely higher.
 */
const savingOn = ({ amount, strikeAmount }) =>
  typeof amount === "number" &&
  typeof strikeAmount === "number" &&
  strikeAmount > amount
    ? strikeAmount - amount
    : null;

/**
 * Renders age based fares as a stacked list, highest fare first, so the
 * headline number a customer sees is the one most of them will actually pay
 * (adult), with the cheaper slab (child/infant) right below it. This mirrors
 * how ticketing sites quote per-traveller-type pricing instead of hiding the
 * variation behind an "onwards" figure.
 *
 * A tier may carry a `strikeAmount` — the pre-discount fare. It renders as a
 * struck out "was" price plus a "Save ₹x" pill, because a lone struck number
 * is easy to misread as the price that applies; the pill says outright that
 * the headline figure is discounted.
 * @param {Array} tiers - [{ label, ageText, amount, strikeAmount }] in any order
 */
const FareTiers = ({ tiers, note, size, tone, offerLabel }) => {
  const ordered = [...tiers].sort((a, b) => b.amount - a.amount);

  return (
    <span className={`fare-tiers fare-tiers--${size} fare-tiers--${tone}`}>
      {offerLabel && <span className="fare-tiers__offer">{offerLabel}</span>}
      {ordered.map((tier, i) => {
        const saving = savingOn(tier);
        const discounted = tier.strikeAmount != null;

        return (
          <span
            key={tier.label + tier.amount}
            className={`fare-tier ${
              i === 0 ? "fare-tier--primary" : "fare-tier--secondary"
            }${discounted ? " fare-tier--discounted" : ""}`}
          >
            <span className="fare-tier__amount">
              {formatAmount(tier.amount)}
            </span>
            {discounted && (
              <span className="fare-tier__strike">
                <span className="fare-tiers__sr">was </span>
                {formatAmount(tier.strikeAmount)}
              </span>
            )}
            {saving !== null && (
              <span className="fare-tier__save">
                Save {formatAmount(saving)}
              </span>
            )}
            <span className="fare-tier__label">
              {tier.label}
              {tier.ageText && (
                <span className="fare-tier__age">{tier.ageText}</span>
              )}
            </span>
          </span>
        );
      })}
      {note && <span className="fare-tiers__note">{note}</span>}
    </span>
  );
};

FareTiers.propTypes = {
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      ageText: PropTypes.string,
      amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
      strikeAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ).isRequired,
  note: PropTypes.node,
  size: PropTypes.oneOf(["lg", "sm"]),
  tone: PropTypes.oneOf(["default", "gold"]),
  // Names the offer the discounted fares belong to, e.g. "Limited-period offer".
  offerLabel: PropTypes.node,
};

FareTiers.defaultProps = {
  note: null,
  size: "lg",
  tone: "default",
  offerLabel: null,
};

export default FareTiers;
